'use client'

import { Command as CommandPrimitive } from 'cmdk'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'

const Command = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'text-popover-foreground relative flex h-full w-full flex-col rounded-md bg-shades-0',
      className,
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

const CommandInput = React.forwardRef(
  ({ className, isOpen, ...props }, ref) => (
    <div className="relative flex items-center" cmdk-input-wrapper="">
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'outline-hidden flex h-11 w-full rounded-md bg-transparent px-3 py-3 text-p-2 font-semibold text-neutral-blue-900 placeholder:text-neutral-blue-500 disabled:cursor-not-allowed disabled:opacity-50 desktop-xs:h-[57px]',
          className,
        )}
        {...props}
      />
      <ChevronDown
        className={cn(
          'pointer-events-none mr-3 size-6 select-none stroke-black transition-transform duration-200',
          {
            'rotate-180': isOpen,
          },
        )}
      />
    </div>
  ),
)

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(
      'max-h-[300px] overflow-y-auto overflow-x-hidden rounded-b-md bg-shades-0',
      className,
    )}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="px-2 py-2 text-p-2 font-semibold text-neutral-blue-900"
    {...props}
  />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      '**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-neutral-blue-500 overflow-hidden p-1 text-neutral-blue-900',
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('bg-border -mx-1 h-px', className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "data-[selected=true]:text-primary-foreground outline-hidden relative mr-1 grid min-h-8 cursor-default select-none grid-cols-[4px_1fr] items-center gap-3 rounded-r-md pr-2 text-p-2 font-semibold text-neutral-blue-900 transition-all ease-in-out data-[disabled=true]:pointer-events-none data-[selected='true']:bg-[#dbdcdd] data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className,
    )}
    {...props}
  />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({ className, ...props }) => {
  return (
    <span
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
}
